/**
 * Hold Creation — capability-owned action vocabulary.
 *
 * Per the Canonical Policy Principle: conditions are shared infrastructure
 * (the policy-conditions field registry), actions are capability-specific.
 * This module is the *only* place that declares what Hold Creation can do
 * in response to a matched condition tree.
 *
 * No generic "anything can do anything" escape hatch. The AI compiler,
 * runtime evaluator, and manual editor all validate against `ActionZ`.
 */
import { z } from "zod";
import { HOLD_REASONS } from "@/lib/hold-rules.functions";

export const APPROVER_ROLES = ["manager", "supervisor"] as const;

export const PlaceHoldZ = z.object({
  kind: z.literal("place_hold"),
  params: z.object({
    reason: z.enum(HOLD_REASONS),
    note: z.string().max(280).optional(),
  }).strict(),
});

export const RequireApprovalZ = z.object({
  kind: z.literal("require_approval"),
  params: z.object({
    approver_role: z.enum(APPROVER_ROLES),
    reason: z.enum(HOLD_REASONS),
  }).strict(),
});

export const AssignHoldCategoryZ = z.object({
  kind: z.literal("assign_hold_category"),
  params: z.object({
    category: z.string().min(1).max(80),
    reason: z.enum(HOLD_REASONS),
  }).strict(),
});

export const HoldCreationActionZ = z.discriminatedUnion("kind", [
  PlaceHoldZ,
  RequireApprovalZ,
  AssignHoldCategoryZ,
]);
export type HoldCreationAction = z.infer<typeof HoldCreationActionZ>;

export const ACTION_KINDS = ["place_hold", "require_approval", "assign_hold_category"] as const;
export type HoldCreationActionKind = (typeof ACTION_KINDS)[number];

/** Validate an actions[] array; throws a readable error on first failure. */
export function validateActions(actions: unknown[]): HoldCreationAction[] {
  if (!Array.isArray(actions) || actions.length === 0) {
    throw new Error("E_ACTIONS: at least one action is required.");
  }
  return actions.map((a, i) => {
    const r = HoldCreationActionZ.safeParse(a);
    if (!r.success) {
      const first = r.error.issues[0];
      throw new Error(
        `E_ACTIONS[${i}]: ${first?.path.join(".") || "kind"}: ${first?.message ?? "invalid"}`,
      );
    }
    return r.data;
  });
}